import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Flag, Loader2, MessagesSquare, RefreshCcw, Settings2, ShieldAlert, Trash2 } from 'lucide-react';
import api from '@/lib/axios';

const ESTADOS = ['', 'aberta', 'resolvida', 'encerrada', 'bloqueada_moderacao'];
const CATEGORIAS = ['', 'aluguer', 'venda', 'motorista'];
const MOTIVOS_ACESSO = [
  'Suporte ao utilizador',
  'Investigação de denúncia',
  'Prevenção de fraude',
  'Cumprimento dos Termos',
  'Segurança',
  'Obrigação legal',
];

const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-PT') : '—');

export default function MarketplaceConversationsAdminPanel() {
  const [filters, setFilters] = useState({
    conversation_id: '', listing_id: '', categoria: '', titulo: '', estado: '',
    email: '', telefone: '', com_denuncia: false, sinalizadas: false, restrito: false,
    data_de: '', data_ate: '',
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState(null);
  const [content, setContent] = useState(null);
  const [motivo, setMotivo] = useState(MOTIVOS_ACESSO[1]);
  const [motivoLivre, setMotivoLivre] = useState('');
  const [reports, setReports] = useState([]);
  const [config, setConfig] = useState(null);
  const [retention, setRetention] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === false) return;
        params.append(k, v);
      });
      const res = await api.get(`/admin/marketplace/conversas?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar conversas.'); }
    finally { setLoading(false); }
  }, [filters]);

  const loadReports = useCallback(async () => {
    try {
      const res = await api.get('/admin/marketplace/conversas/denuncias');
      setReports(res.data.items || []);
    } catch (_) {}
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/marketplace/conversas/config');
      setConfig(res.data);
    } catch (_) {}
  }, []);

  useEffect(() => { load(); loadReports(); loadConfig(); }, [load, loadReports, loadConfig]);

  const openDetail = async (id) => {
    setBusy(true); setContent(null); setMsg('');
    try {
      const res = await api.get(`/admin/marketplace/conversas/${id}`);
      setDetail(res.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a abrir a conversa.'); }
    finally { setBusy(false); }
  };

  const openContent = async () => {
    const razao = motivo === 'Outro' ? motivoLivre.trim() : motivo;
    if (!razao || razao.length < 5) { setErr('Indique o motivo (mínimo 5 caracteres).'); return; }
    setBusy(true); setErr('');
    try {
      const res = await api.post(`/admin/marketplace/conversas/${detail.id}/abrir`, { motivo: razao });
      setContent(res.data.items || []);
      setMsg('Acesso registado na auditoria.');
      await openDetailSilent(detail.id);
    } catch (e) { setErr(e.response?.data?.detail || 'Não foi possível abrir o conteúdo.'); }
    finally { setBusy(false); }
  };

  const openDetailSilent = async (id) => {
    try {
      const res = await api.get(`/admin/marketplace/conversas/${id}`);
      setDetail(res.data);
    } catch (_) {}
  };

  const action = async (acao, extra = {}) => {
    const razao = (extra.motivo || motivoLivre || motivo || '').trim();
    if (razao.length < 3) { setErr('Indique o motivo da ação.'); return; }
    setBusy(true); setErr('');
    try {
      await api.post(`/admin/marketplace/conversas/${detail.id}/acao`, { acao, motivo: razao, ...extra });
      setMsg(`Ação "${acao}" aplicada.`);
      if (extra.message_id) {
        const oculta = acao === 'ocultar_mensagem';
        setContent((prev) => (prev || []).map((m) => (m.id === extra.message_id ? { ...m, oculta } : m)));
      }
      await openDetailSilent(detail.id);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Ação não aplicada.'); }
    finally { setBusy(false); }
  };

  const updateReport = async (id, estado) => {
    setBusy(true);
    try {
      await api.put(`/admin/marketplace/conversas/denuncias/${id}`, { estado, nota: motivoLivre || '' });
      await loadReports();
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a atualizar a denúncia.'); }
    finally { setBusy(false); }
  };

  const saveConfig = async () => {
    setBusy(true); setErr('');
    try {
      const res = await api.put('/admin/marketplace/conversas/config', {
        sessao_dias: Number(config.sessao_dias),
        retencao_conversas_dias: Number(config.retencao_conversas_dias),
        retencao_denuncias_dias: Number(config.retencao_denuncias_dias),
        retencao_auditoria_dias: Number(config.retencao_auditoria_dias),
        eliminacao_automatica: !!config.eliminacao_automatica,
        agrupamento_minutos: Number(config.agrupamento_minutos),
        notificar_por_email: !!config.notificar_por_email,
        limite_msgs_minuto: Number(config.limite_msgs_minuto),
        limite_msgs_dia: Number(config.limite_msgs_dia),
        max_caracteres: Number(config.max_caracteres),
        bloquear_links: !!config.bloquear_links,
        palavras_ofensivas: (config.palavras_ofensivas || []),
      });
      setConfig(res.data);
      setMsg('Configuração guardada.');
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a guardar a configuração.'); }
    finally { setBusy(false); }
  };

  const simulate = async (dryRun) => {
    setBusy(true);
    try {
      const res = dryRun
        ? await api.post('/admin/marketplace/conversas/retencao/simular')
        : await api.post('/admin/marketplace/conversas/retencao/executar?dry_run=false');
      setRetention(res.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha na operação de retenção.'); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-conversations-panel">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900">
          <MessagesSquare className="h-4 w-4 text-emerald-600" /> Conversas e denúncias
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="admin-conv-reload">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </header>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="ID da conversa"
          value={filters.conversation_id} onChange={(e) => setFilters((f) => ({ ...f, conversation_id: e.target.value }))} data-testid="admin-conv-filter-id" />
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="ID do anúncio"
          value={filters.listing_id} onChange={(e) => setFilters((f) => ({ ...f, listing_id: e.target.value }))} data-testid="admin-conv-filter-listing" />
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Título do anúncio"
          value={filters.titulo} onChange={(e) => setFilters((f) => ({ ...f, titulo: e.target.value }))} data-testid="admin-conv-filter-title" />
        <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" value={filters.categoria}
          onChange={(e) => setFilters((f) => ({ ...f, categoria: e.target.value }))} data-testid="admin-conv-filter-category">
          {CATEGORIAS.map((c) => <option key={c} value={c}>{c || 'Todos os tipos'}</option>)}
        </select>
        <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" value={filters.estado}
          onChange={(e) => setFilters((f) => ({ ...f, estado: e.target.value }))} data-testid="admin-conv-filter-state">
          {ESTADOS.map((c) => <option key={c} value={c}>{c || 'Todos os estados'}</option>)}
        </select>
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Email exato"
          value={filters.email} onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))} data-testid="admin-conv-filter-email" />
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Telefone exato"
          value={filters.telefone} onChange={(e) => setFilters((f) => ({ ...f, telefone: e.target.value }))} data-testid="admin-conv-filter-phone" />
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={filters.com_denuncia}
            onChange={(e) => setFilters((f) => ({ ...f, com_denuncia: e.target.checked }))} data-testid="admin-conv-filter-reported" /> Com denúncia</label>
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={filters.sinalizadas}
            onChange={(e) => setFilters((f) => ({ ...f, sinalizadas: e.target.checked }))} data-testid="admin-conv-filter-flagged" /> Sinalizadas</label>
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={filters.restrito}
            onChange={(e) => setFilters((f) => ({ ...f, restrito: e.target.checked }))} data-testid="admin-conv-filter-restricted" /> Restritos</label>
        </div>
      </div>

      {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="admin-conv-error">{err}</p>}
      {msg && <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="admin-conv-msg">{msg}</p>}

      <div className="mt-3 overflow-x-auto">
        {loading ? (
          <p className="flex items-center gap-2 py-6 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-xs text-slate-500" data-testid="admin-conv-empty">Sem conversas para os filtros aplicados.</p>
        ) : (
          <table className="w-full min-w-[860px] text-left text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="py-2">Anúncio</th><th>Participantes</th><th>Estado</th><th>Msg</th><th>Denúncias</th><th>Última</th><th /></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`admin-conv-row-${c.id}`}>
                  <td className="py-2">
                    <p className="font-semibold text-slate-800">{c.anuncio?.titulo}</p>
                    <p className="text-[10px] text-slate-400">{c.anuncio?.categoria} · {c.anuncio?.estado}</p>
                  </td>
                  <td>
                    {(c.participantes || []).map((p) => (
                      <p key={p.id} className="text-[11px] text-slate-600">
                        {p.papel === 'anunciante' ? 'A' : 'I'}: {p.nome_publico} · {p.email}
                        {p.bloqueado_admin ? ' · bloqueado' : ''}{p.silenciado_ate ? ' · silenciado' : ''}
                      </p>
                    ))}
                  </td>
                  <td>{c.estado}</td>
                  <td>{c.mensagens}</td>
                  <td>{c.denuncias > 0 ? <span className="font-bold text-rose-600">{c.denuncias}</span> : 0}</td>
                  <td className="whitespace-nowrap text-[11px] text-slate-500">{quando(c.last_message_at)}</td>
                  <td>
                    <button type="button" onClick={() => openDetail(c.id)} className="rounded-lg border border-slate-300 px-2 py-1 text-[11px] font-semibold" data-testid={`admin-conv-open-${c.id}`}>Abrir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div className="mt-4 rounded-2xl border border-slate-300 bg-slate-50 p-3" data-testid="admin-conv-detail">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-slate-900">{detail.anuncio?.titulo}</p>
            <button type="button" onClick={() => { setDetail(null); setContent(null); }} className="text-xs font-semibold text-slate-500 underline" data-testid="admin-conv-close">Fechar</button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Conversa {detail.id} · {detail.mensagens} mensagens · {detail.estado}</p>

          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {(detail.participantes || []).map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-2 text-[11px]" data-testid={`admin-conv-participant-${p.id}`}>
                <p className="font-semibold text-slate-800">{p.papel} · {p.nome_declarado || p.nome_publico}</p>
                <p className="text-slate-500">{p.email} · {p.telefone}</p>
                <p className="text-slate-500">
                  {p.email_verificado ? 'Email verificado' : 'Email não verificado'}
                  {p.telefone_verificado ? ' · Telefone verificado' : ''}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <button type="button" onClick={() => action('avisar', { participante_id: p.id })} className="rounded border border-slate-300 px-1.5 py-0.5" data-testid={`admin-conv-warn-${p.id}`}>Avisar</button>
                  <button type="button" onClick={() => action('silenciar', { participante_id: p.id, dias: 7 })} className="rounded border border-slate-300 px-1.5 py-0.5" data-testid={`admin-conv-mute-${p.id}`}>Silenciar 7d</button>
                  <button type="button" onClick={() => action(p.bloqueado_admin ? 'desbloquear_participante' : 'bloquear_participante', { participante_id: p.id })} className="rounded border border-slate-300 px-1.5 py-0.5" data-testid={`admin-conv-block-${p.id}`}>{p.bloqueado_admin ? 'Desbloquear' : 'Bloquear'}</button>
                  <button type="button" onClick={() => action('bloquear_contacto', { participante_id: p.id })} className="rounded border border-slate-300 px-1.5 py-0.5" data-testid={`admin-conv-blockcontact-${p.id}`}>Bloquear contacto</button>
                  <button type="button" onClick={() => action('revogar_sessoes', { participante_id: p.id })} className="rounded border border-slate-300 px-1.5 py-0.5" data-testid={`admin-conv-revoke-${p.id}`}>Revogar sessões</button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-2">
            <p className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-900">
              <ShieldAlert className="h-3.5 w-3.5" /> Consultar mensagens exige motivo registado em auditoria
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <select value={motivo} onChange={(e) => setMotivo(e.target.value)} className="rounded-lg border border-amber-300 px-2 py-1.5 text-xs" data-testid="admin-conv-reason">
                {[...MOTIVOS_ACESSO, 'Outro'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input value={motivoLivre} onChange={(e) => setMotivoLivre(e.target.value)} placeholder="Motivo detalhado (obrigatório se 'Outro')"
                className="min-w-[220px] flex-1 rounded-lg border border-amber-300 px-2 py-1.5 text-xs" data-testid="admin-conv-reason-free" />
              <button type="button" onClick={openContent} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid="admin-conv-open-content">
                <Eye className="h-3.5 w-3.5" /> Abrir conteúdo
              </button>
            </div>
          </div>

          {content && (
            <div className="mt-3 max-h-72 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2" data-testid="admin-conv-content">
              {content.map((m) => (
                <div key={m.id} className="border-b border-slate-100 pb-1 text-[11px]">
                  <p className="text-slate-400">{quando(m.created_at)} · {m.sender_role} {m.oculta ? '· OCULTA' : ''} {(m.moderacao?.regras || []).length ? `· ${m.moderacao.regras.join(', ')}` : ''}</p>
                  <p className="whitespace-pre-wrap text-slate-800">{m.texto}</p>
                  {m.tipo !== 'sistema' && (
                    <button type="button" onClick={() => action(m.oculta ? 'restaurar_mensagem' : 'ocultar_mensagem', { message_id: m.id })}
                      className="mt-0.5 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`admin-conv-hide-${m.id}`}>
                      {m.oculta ? 'Restaurar' : 'Ocultar'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={() => action('encerrar')} className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold" data-testid="admin-conv-terminate">Encerrar conversa</button>
            <button type="button" onClick={() => action('reabrir')} className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold" data-testid="admin-conv-reopen">Reabrir conversa</button>
            <button type="button" onClick={() => action('nota')} className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold" data-testid="admin-conv-note">Guardar nota interna</button>
          </div>

          <details className="mt-3" data-testid="admin-conv-audit">
            <summary className="cursor-pointer text-xs font-bold text-slate-700">Histórico de moderação e auditoria ({(detail.historico || []).length})</summary>
            <ul className="mt-1 space-y-1 text-[11px] text-slate-600">
              {(detail.historico || []).map((h) => (
                <li key={h.id}>{quando(h.created_at)} · {h.acao} · {h.admin_email || 'sistema'} · {h.motivo}</li>
              ))}
            </ul>
          </details>
        </div>
      )}

      <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50/50 p-3" data-testid="admin-conv-reports">
        <p className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900"><Flag className="h-4 w-4 text-rose-600" /> Denúncias das conversas ({reports.length})</p>
        {reports.length === 0 ? (
          <p className="mt-1 text-xs text-slate-500" data-testid="admin-conv-reports-empty">Sem denúncias registadas.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {reports.map((r) => (
              <li key={r.id} className="rounded-xl border border-slate-200 bg-white p-2 text-[11px]" data-testid={`admin-conv-report-${r.id}`}>
                <p className="font-semibold text-slate-800">{r.motivo} · {r.estado} · {quando(r.created_at)}</p>
                <p className="text-slate-600">{r.descricao || 'Sem descrição'}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <button type="button" onClick={() => openDetail(r.conversation_id)} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`admin-conv-report-open-${r.id}`}>Ver conversa</button>
                  <button type="button" onClick={() => updateReport(r.id, 'resolvida')} className="rounded border border-emerald-300 px-1.5 py-0.5 font-semibold text-emerald-700" data-testid={`admin-conv-report-resolve-${r.id}`}>Resolvida</button>
                  <button type="button" onClick={() => updateReport(r.id, 'improcedente')} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`admin-conv-report-dismiss-${r.id}`}>Improcedente</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {config && (
        <div className="mt-5 rounded-2xl border border-slate-200 p-3" data-testid="admin-conv-config">
          <p className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900"><Settings2 className="h-4 w-4 text-emerald-600" /> Configuração e conservação</p>
          <p className="mt-1 text-[11px] text-slate-500">
            Conversas ativas no servidor: <strong>{config.conversas_ativas ? 'sim' : 'não'}</strong> (controlado por MARKETPLACE_CONVERSATIONS_ENABLED).
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {[['sessao_dias', 'Duração da sessão (dias)'], ['agrupamento_minutos', 'Agrupar notificações (min)'],
              ['retencao_conversas_dias', 'Conservar conversas (dias)'], ['retencao_denuncias_dias', 'Conservar denúncias (dias)'],
              ['retencao_auditoria_dias', 'Conservar auditoria (dias)'], ['limite_msgs_minuto', 'Máx. mensagens/minuto'],
              ['limite_msgs_dia', 'Máx. mensagens/dia'], ['max_caracteres', 'Máx. caracteres']].map(([key, label]) => (
              <label key={key} className="text-[11px] text-slate-600">
                {label}
                <input type="number" value={config[key] ?? ''} onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
                  className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid={`admin-conv-config-${key}`} />
              </label>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-600">
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!config.notificar_por_email}
              onChange={(e) => setConfig((c) => ({ ...c, notificar_por_email: e.target.checked }))} data-testid="admin-conv-config-notify" /> Notificar por email</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!config.bloquear_links}
              onChange={(e) => setConfig((c) => ({ ...c, bloquear_links: e.target.checked }))} data-testid="admin-conv-config-links" /> Bloquear links</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!config.eliminacao_automatica}
              onChange={(e) => setConfig((c) => ({ ...c, eliminacao_automatica: e.target.checked }))} data-testid="admin-conv-config-auto" /> Eliminação automática (aguarda validação jurídica)</label>
          </div>
          <label className="mt-2 block text-[11px] text-slate-600">
            Palavras ofensivas (uma por linha)
            <textarea rows={3} value={(config.palavras_ofensivas || []).join('\n')}
              onChange={(e) => setConfig((c) => ({ ...c, palavras_ofensivas: e.target.value.split('\n').map((w) => w.trim()).filter(Boolean) }))}
              className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-conv-config-words" />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={saveConfig} disabled={busy} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid="admin-conv-config-save">Guardar configuração</button>
            <button type="button" onClick={() => simulate(true)} disabled={busy} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold" data-testid="admin-conv-retention-sim">Simular eliminação</button>
            <button type="button" onClick={() => simulate(false)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700" data-testid="admin-conv-retention-run">
              <Trash2 className="h-3.5 w-3.5" /> Executar eliminação
            </button>
          </div>
          {retention && (
            <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-2 text-[10px] text-emerald-200" data-testid="admin-conv-retention-result">{JSON.stringify(retention, null, 2)}</pre>
          )}
        </div>
      )}
    </section>
  );
}
